import Layout from "@/components/layout/Layout";
import React, { useState } from "react";
import Sidepanel from "@/components/layout/Sidepanel";
import Content from "@/components/layout/Content";
import ContentOptions from "@/components/layout/ContentOptions";
import Heading from "@/components/elements/Heading";
import NavigationButtons from "@/components/elements/Buttons";
import Label from "@/components/elements/Label";
import Dropdown from "@/components/elements/Dropdown";
import NumberInputFinishingAll from "@/components/elements/NumberInputFinishingAll";
import Drawing from "@/components/drawing/drawing";
import api from "@/services/api";

/**
 * FinishingAll (All-Sides Finishing) Configuration Component
 *
 * Handles the configuration of finishing options when all sides of the product
 * should have identical groove/edge finishing. Allows users to specify the
 * side preference (inside/outside) and dimensional specifications that apply
 * to all four sides of the product.
 *
 * @param {Object} props - Component props
 * @param {Object} props.session - Current session information
 * @param {string} props.shoppingurl - URL for shopping cart navigation
 * @param {string} props.step - Current configuration step identifier
 * @param {Array} props.steps - Available configuration steps
 * @param {Object} props.product - Product configuration data
 * @param {Object} props.product.properties - Current product properties
 * @param {string} props.heading - Page heading text
 * @param {string} props.subheading - Page subheading text
 * @param {Array} props.questions - Configuration questions
 * @param {string} props.saveUrl - API endpoint for saving configuration
 * @param {string} props.prevLink - URL for previous step navigation
 * @param {string} props.nextLink - URL for next step navigation
 * @param {Array} props.colors - Available color options for visualization
 *
 * @returns {JSX.Element} The rendered all-sides finishing configuration page
 */
export default function FinishingAll({
    session,
    shoppingurl,
    step,
    steps,
    product,
    heading,
    subheading,
    questions,
    saveUrl,
    prevLink,
    nextLink,
    colors,
}) {
    // Initialize state with current product data
    const [productprops, setProductprops] = useState(product);
    const [properties, setProperties] = useState(product.properties);
    const [Questions, setQuestions] = useState(questions);
    const [stepList, setStepList] = useState(steps);

    /**
     * Update properties with optional server persistence
     * Handles property updates and conditionally saves to server
     *
     * @param {Object} properties - Updated properties object
     * @param {boolean} doRequest - Whether to save to server (default: true)
     */
    function updateProps(properties, doRequest = true) {
        setProperties(properties);
        if (doRequest) {
            api.put(saveUrl, { properties: properties });
        }
    }

    /**
     * Handle side preference changes with comprehensive property updates
     * When the finishing side (inside/outside) changes, updates all related
     * groove properties for all four sides of the product
     *
     * @param {Object} value - Updated properties object
     * @param {string} property - Property being updated (typically sponning_rondom)
     */
    function updateQuestion(value, property) {
        let props = { ...value };

        // Define all groove dimension properties for each side
        let breedte = ["BO_BR", "LI_BR", "RE_BR", "O_BR"]; // Width: Top, Left, Right, Bottom
        let hoogte = ["BO_DI", "LI_DI", "RE_DI", "O_DI"];   // Height: Top, Left, Right, Bottom

        // Reset all groove properties and apply new side preference
        breedte.map((item) => {
            props["SP_BU_" + item] = 0;  // Clear outside groove settings
            props["SP_BI_" + item] = 0;  // Clear inside groove settings
            props[value[property] + "_" + item] = properties.sponning_rondom_breedte;
        });

        hoogte.map((item) => {
            props["SP_BU_" + item] = 0;  // Clear outside groove settings
            props["SP_BI_" + item] = 0;  // Clear inside groove settings
            props[value[property] + "_" + item] = properties.sponning_rondom_hoogte;
        });

        // Update individual side preferences if this is the global side setting
        if (property === "sponning_rondom") {
            let sponning = ["boven", "onder", "links", "rechts"];
            sponning.map((item) => {
                props["sponning_" + item] = value[property];
            });
        }

        setProperties(props);

        // Save to server and refresh product data
        api.put(saveUrl, { properties: props }).then((response) => {
            setProductprops(response.data);
        });
    }

    /**
     * Generate property variable name for groove dimensions
     * Combines the current side preference with the specific dimension type
     *
     * @param {string} variable - The dimension type (e.g., "BO_BR", "LI_DI")
     * @returns {string} Complete property name (e.g., "SP_BU_BO_BR")
     */
    const getVariable = (variable) => {
        return properties["sponning_rondom"] + "_" + variable;
    };

    return (
        <Layout>
            <Sidepanel
                steps={stepList}
                shoppingurl={shoppingurl}
                session={session}
                product={product}
                step={step}
            />
            <Content
                steps={stepList}
                step={step}
                shoppingurl={shoppingurl}
                session={session}
                product={product}
            >
                <ContentOptions>
                    <Heading heading={heading} subheading={subheading} />

                    <div className="question">
                        <div className="d-block">
                            <Label label="Keuze zijdes" />
                            <Dropdown
                                properties={properties}
                                property={"sponning_rondom"}
                                updateProperties={updateQuestion}
                                options={[
                                    { name: "Buiten", value: "SP_BU" },
                                    { name: "Binnen", value: "SP_BI" },
                                ]}
                            />
                        </div>
                        <div className="d-block">
                            <Label label="Sponningmaat breedte" />
                            <NumberInputFinishingAll
                                properties={properties}
                                property={"sponning_rondom_breedte"}
                                needsvalidation
                                field={{ min: 1, max: 1000000, unit: "mm" }}
                                updateProperties={updateProps}
                            />
                        </div>
                        <div className="d-block">
                            <Label label="Sponningmaat hoogte" />
                            <NumberInputFinishingAll
                                properties={properties}
                                property={"sponning_rondom_hoogte"}
                                field={{ min: 0, max: 1000000, unit: "mm" }}
                                updateProperties={updateProps}
                            />
                        </div>
                    </div>

                    <NavigationButtons
                        prevlink={prevLink}
                        nextlink={nextLink}
                    />
                </ContentOptions>
                <Drawing
                    session_product={productprops}
                    properties={properties}
                    products={product.products}
                    sidepanel
                    sponningside={properties.sponning_rondom}
                    colors={colors}
                    afwerkingactive
                />
            </Content>
        </Layout>
    );
}
